/**
 * Summary numbers for one track over a range, and the axis they are drawn on.
 *
 * Nothing here is computed anywhere else. A component that works out its own
 * average will eventually disagree with the PDF report generated from the same
 * data, and the person holding both has no way to tell which is wrong.
 *
 * Every function takes a `QuantitativeSeries`, so an unordered enum cannot
 * reach them: there is no overload that accepts one and no null-returning
 * escape hatch that a caller might default to zero. The restriction is in the
 * type, which is the only place it cannot be forgotten.
 */

import { deriveDecimals, deriveStep, tickValues } from '../model/scale.js'
import type { EnumKind, Track } from '../model/track.js'
import { matchTrackKind } from '../model/track.js'
import type { QuantitativePoint, QuantitativeSeries } from './series.js'

const DAY_MS = 86_400_000

export interface Statistics {
  readonly count: number
  readonly mean: number
  readonly median: number
  readonly min: number
  readonly max: number
  /**
   * Population standard deviation, dividing by n rather than n − 1.
   *
   * These are not a sample of a wider population — they are every measurement
   * recorded. The panel answers "how much did what I logged vary", which is a
   * description of the data in hand, not an inference about data that is not.
   */
  readonly standardDeviation: number
  readonly first: QuantitativePoint
  readonly last: QuantitativePoint
}

function sorted(values: readonly number[]): number[] {
  return [...values].sort((a, b) => a - b)
}

function medianOf(ascending: readonly number[]): number {
  const middle = Math.floor(ascending.length / 2)
  if (ascending.length % 2 === 1) {
    return ascending[middle] ?? 0
  }
  return ((ascending[middle - 1] ?? 0) + (ascending[middle] ?? 0)) / 2
}

/** `null` for an empty series: there is no mean of nothing, and zero is a lie. */
export function statisticsFor(series: QuantitativeSeries): Statistics | null {
  const { points } = series
  const first = points[0]
  const last = points[points.length - 1]
  if (first === undefined || last === undefined) {
    return null
  }

  const quantities = points.map((point) => point.quantity)
  const mean = quantities.reduce((total, value) => total + value, 0) / quantities.length
  const variance =
    quantities.reduce((total, value) => total + (value - mean) ** 2, 0) /
    quantities.length
  const ascending = sorted(quantities)

  return {
    count: quantities.length,
    mean,
    median: medianOf(ascending),
    min: ascending[0] ?? 0,
    max: ascending[ascending.length - 1] ?? 0,
    standardDeviation: Math.sqrt(variance),
    first,
    last,
  }
}

// ─── Trend ────────────────────────────────────────────────────────────────────

/**
 * Below this many points, no line is fitted.
 *
 * A regression through three self-reported readings is arithmetic, not
 * evidence, and it renders identically to one through three hundred. Refusing
 * is the honest output — see the ticket note on statistical claims in a
 * document someone makes decisions from.
 */
export const MINIMUM_TREND_POINTS = 5

/**
 * The smallest change a track can record, or `null` where it has none.
 *
 * This is the floor under the steady band below, and the reason is arithmetic
 * rather than taste. A band made only of observed scatter is zero when the
 * readings happen to fall exactly on a line — which real self-reported data
 * does more often than it sounds, because people round — and a zero band calls
 * a hundredth of a point a trend. Nothing smaller than one step can be
 * recorded, so nothing smaller than one step can be movement.
 */
export function smallestStep(track: Track): number | null {
  return matchTrackKind<number | null>(track.kind, {
    numeric: (kind) => kind.step,
    boolean: () => 1,
    enum: (kind) => {
      if (!kind.ordered) {
        return null
      }
      const scores = [
        ...new Set(
          kind.options
            .map((option) => option.score)
            .filter((score): score is number => score !== null),
        ),
      ].sort((a, b) => a - b)
      if (scores.length < 2) {
        return null
      }
      // The tightest gap, not the average one: an unevenly scored scale can
      // still distinguish its closest pair, and that is what "smallest
      // recordable change" means.
      let smallest = Number.POSITIVE_INFINITY
      for (let index = 1; index < scores.length; index += 1) {
        smallest = Math.min(smallest, (scores[index] ?? 0) - (scores[index - 1] ?? 0))
      }
      return smallest
    },
  })
}

export interface FittedTrend {
  readonly kind: 'trend'
  readonly direction: 'rising' | 'falling' | 'steady'
  /** Modelled change per day, in the track's own units. */
  readonly perDay: number
  /** Modelled change across the observed span, first point to last. */
  readonly change: number
  /** 0–1. How much of the variation the line accounts for. */
  readonly rSquared: number
  readonly spanDays: number
}

export interface InsufficientData {
  readonly kind: 'insufficientData'
  readonly points: number
  readonly required: number
}

export type Trend = FittedTrend | InsufficientData

export interface TrendOptions {
  readonly minimumPoints?: number
}

/**
 * Least-squares fit over time, or an explicit refusal.
 *
 * The refusal is a variant of the result rather than a `null`, so a caller
 * cannot fall through to rendering an arrow that means nothing. Points sharing
 * a single instant also refuse: there is no slope through a vertical line.
 */
export function trendOf(series: QuantitativeSeries, options: TrendOptions = {}): Trend {
  const required = options.minimumPoints ?? MINIMUM_TREND_POINTS
  const { points } = series

  if (points.length < required) {
    return { kind: 'insufficientData', points: points.length, required }
  }

  const origin = points[0]?.at.epochMs ?? 0
  const xs = points.map((point) => (point.at.epochMs - origin) / DAY_MS)
  const ys = points.map((point) => point.quantity)
  const spanDays = (xs[xs.length - 1] ?? 0) - (xs[0] ?? 0)
  if (spanDays <= 0) {
    return { kind: 'insufficientData', points: points.length, required }
  }

  const n = xs.length
  const meanX = xs.reduce((total, value) => total + value, 0) / n
  const meanY = ys.reduce((total, value) => total + value, 0) / n
  let covariance = 0
  let varianceX = 0
  for (let index = 0; index < n; index += 1) {
    const dx = (xs[index] ?? 0) - meanX
    covariance += dx * ((ys[index] ?? 0) - meanY)
    varianceX += dx * dx
  }
  if (varianceX === 0) {
    return { kind: 'insufficientData', points: n, required }
  }

  const perDay = covariance / varianceX
  const intercept = meanY - perDay * meanX
  const change = perDay * spanDays

  let residual = 0
  let total = 0
  for (let index = 0; index < n; index += 1) {
    const predicted = intercept + perDay * (xs[index] ?? 0)
    residual += ((ys[index] ?? 0) - predicted) ** 2
    total += ((ys[index] ?? 0) - meanY) ** 2
  }
  // A flat series has no variation to explain; the line accounts for all of it.
  const rSquared = total === 0 ? 1 : Math.max(0, 1 - residual / total)

  /*
   * The steady band follows the readings, not the track's declared bounds.
   *
   * It used to be a twentieth of the declared range, which coupled the verdict
   * to a setup choice nobody makes with trends in mind. Bounds exist to size
   * the input control, so people pick them generously — a weight track
   * declared 40–150kg got a 5.5kg band, and a real, visible four-kilo loss
   * came back as "steady" while the chart beside it sloped down. Two things on
   * one screen disagreeing is how an app loses the user's trust, and the cause
   * was invisible: nothing tells you the maximum you typed months ago is what
   * decided it.
   *
   * So compare the movement against the scatter of the readings around the
   * fitted line instead. That is the question a person is actually asking —
   * *is this bigger than my normal wobble?* — and it is answered by the same
   * data the chart draws, so the label and the picture agree. Two tracks with
   * identical readings now get identical verdicts however their bounds were
   * declared, which is a property worth having on its own.
   *
   * Scatter *around the line*, not overall spread: a strong trend inflates the
   * overall spread, so a band built from that would grow with the very signal
   * it is meant to detect and quietly call the clearest trends steady.
   */
  const residualSd = n > 2 ? Math.sqrt(residual / (n - 2)) : 0
  const band = Math.max(residualSd, smallestStep(series.track) ?? 0)
  const direction =
    Math.abs(change) <= band ? 'steady' : change > 0 ? 'rising' : 'falling'

  return { kind: 'trend', direction, perDay, change, rSquared, spanDays }
}

// ─── The scale a track is measured on ─────────────────────────────────────────

export interface QuantitativeRange {
  readonly min: number
  readonly max: number
}

/**
 * The full range a track can occupy, or `null` where it has none.
 *
 * Declared bounds, deliberately, not the observed ones. A track scored between
 * 5 and 7 on a scale of 1–10 should sit as a band across the middle of its
 * chart; stretching the observed values to fill the axis turns a stable
 * fortnight into a dramatic sawtooth.
 *
 * Enum scores are read from **every** option, archived included, because the
 * history resolves against all of them (T-305).
 */
export function quantitativeRange(track: Track): QuantitativeRange | null {
  return matchTrackKind<QuantitativeRange | null>(track.kind, {
    numeric: (kind) => ({ min: kind.min, max: kind.max }),
    boolean: () => ({ min: 0, max: 1 }),
    enum: (kind) => {
      if (!kind.ordered) {
        return null
      }
      const scores = kind.options
        .map((option) => option.score)
        .filter((score): score is number => score !== null)
      if (scores.length === 0) {
        return null
      }
      return { min: Math.min(...scores), max: Math.max(...scores) }
    },
  })
}

export interface AxisTick {
  readonly quantity: number
  readonly label: string
}

export interface Axis {
  readonly min: number
  readonly max: number
  readonly ticks: readonly AxisTick[]
}

/** Enough ticks to read the scale, few enough to fit on a phone. */
const MAX_AXIS_TICKS = 8

const TICK_MULTIPLES = [1, 2, 5, 10, 20, 50, 100]

function roundish(value: number, decimals: number): number {
  return Number.parseFloat(value.toPrecision(Math.max(decimals, 1) + 10))
}

function thinnedTicks(min: number, max: number, decimals: number): number[] {
  const base = deriveStep(min, max)
  const step =
    TICK_MULTIPLES.map((multiple) => base * multiple).find(
      (candidate) => (max - min) / candidate + 1 <= MAX_AXIS_TICKS,
    ) ?? max - min
  const ticks: number[] = []
  for (let value = min; value <= max + step / 2; value += step) {
    ticks.push(roundish(Math.min(value, max), decimals))
  }
  return [...new Set(ticks)]
}

function orderedOptionTicks(kind: EnumKind): AxisTick[] {
  return kind.options
    .filter(
      (option): option is typeof option & { score: number } => option.score !== null,
    )
    .map((option) => ({ quantity: option.score, label: option.label }))
    .sort((a, b) => a.quantity - b.quantity)
}

/**
 * The y-axis for a track, in the track's own vocabulary.
 *
 * An ordered enum gets one tick per option labelled with the option's name.
 * Showing `0, 1, 2` on a scale whose values are `none, mild, strong` makes the
 * reader translate, and the scores are an implementation detail they never
 * chose. `null` for an unordered enum, which has no axis to draw.
 */
export function axisFor(track: Track): Axis | null {
  return matchTrackKind<Axis | null>(track.kind, {
    numeric: (kind) => {
      const all = tickValues({
        min: kind.min,
        max: kind.max,
        step: kind.step,
        decimals: kind.decimals,
        explicit: { step: true, decimals: true },
      })
      const values =
        all.length <= MAX_AXIS_TICKS
          ? all
          : thinnedTicks(kind.min, kind.max, kind.decimals)
      return {
        min: kind.min,
        max: kind.max,
        ticks: values.map((quantity) => ({
          quantity,
          label: quantity.toFixed(kind.decimals),
        })),
      }
    },
    boolean: (kind) => ({
      min: 0,
      max: 1,
      ticks: [
        { quantity: 0, label: kind.falseLabel },
        { quantity: 1, label: kind.trueLabel },
      ],
    }),
    enum: (kind) => {
      if (!kind.ordered) {
        return null
      }
      const ticks = orderedOptionTicks(kind)
      const first = ticks[0]
      const last = ticks[ticks.length - 1]
      if (first === undefined || last === undefined) {
        return null
      }
      return { min: first.quantity, max: last.quantity, ticks }
    },
  })
}

/**
 * A number put back into the track's own words.
 *
 * Used for aggregates, which are not recorded values and so may land between
 * two options. Saying `between mild and strong` is longer than `1.4` and is
 * what the number actually means; `1.4` invites the reader to believe the scale
 * is metric when the gaps between options are whatever the user felt.
 */
export function describeQuantity(track: Track, quantity: number): string {
  return matchTrackKind<string>(track.kind, {
    numeric: (kind) => quantity.toFixed(kind.decimals),
    boolean: (kind) => {
      if (quantity === 0) {
        return kind.falseLabel
      }
      if (quantity === 1) {
        return kind.trueLabel
      }
      return `${String(Math.round(quantity * 100))}% ${kind.trueLabel.toLowerCase()}`
    },
    enum: (kind) => {
      const ticks = orderedOptionTicks(kind)
      const exact = ticks.find((tick) => tick.quantity === quantity)
      if (exact !== undefined) {
        return exact.label
      }
      const below = [...ticks].reverse().find((tick) => tick.quantity < quantity)
      const above = ticks.find((tick) => tick.quantity > quantity)
      if (below === undefined || above === undefined) {
        return String(quantity)
      }
      return `between ${below.label} and ${above.label}`
    },
  })
}

/**
 * A *change* put into words — which is not the same as a value.
 *
 * `describeQuantity` answers "where is this on the scale"; a trend answers "how
 * far did it move", and the two need different vocabulary. Running a delta of
 * 1.4 through `describeQuantity` on an ordered enum yields "between Mild and
 * Strong", so the panel reads "rising by between Mild and Strong" — which is
 * not a sentence, and worse, implies a position the number does not describe.
 *
 * Units follow the kind: a numeric track's own, percentage points for a rate,
 * and positions on the scale for an ordered enum, whose gaps are whatever the
 * user felt and so cannot be given a unit at all.
 */
export function describeChange(track: Track, change: number): string {
  const size = Math.abs(change)
  return matchTrackKind<string>(track.kind, {
    numeric: (kind) =>
      `${size.toFixed(kind.decimals)}${track.unit === undefined ? '' : ` ${track.unit}`}`,
    boolean: () => {
      const points = Math.round(size * 100)
      return `${String(points)} percentage point${points === 1 ? '' : 's'}`
    },
    enum: () => {
      const steps = Number(size.toFixed(1))
      return `${String(steps)} step${steps === 1 ? '' : 's'} on the scale`
    },
  })
}

/** Decimal places worth showing for an aggregate of this track. */
export function aggregateDecimals(track: Track): number {
  return matchTrackKind<number>(track.kind, {
    numeric: (kind) => Math.max(kind.decimals, deriveDecimals(kind.step)),
    boolean: () => 2,
    enum: () => 1,
  })
}
